/**
 * SkyNexa — enquiry form controller.
 *
 * Wires the project brief form to a real endpoint, with inline per-field
 * validation, accessible error reporting, and a local fallback so a visitor's
 * message is never silently lost when the network fails.
 *
 * ── Configuring delivery ───────────────────────────────────────────────────
 * Set the Web3Forms access key one of two ways:
 *   1. Build-time (preferred): put `VITE_WEB3FORMS_KEY=your-key` in `.env.local`.
 *   2. Markup: add `data-access-key="your-key"` to the <form>.
 * Get a free key at https://web3forms.com — it is a public, per-form key and
 * is safe to ship in client code. Until a key is set the form runs in
 * "preview" mode: it validates fully and reports honestly that delivery is
 * not configured, rather than faking a success message.
 *
 * @module enquiry
 */

import { validateEnquiry, validateField, buildSubmission, LIMITS } from './validation.js';
import { ENQUIRY_INTERESTS, PRACTICE_TO_INTEREST } from '../data/technologies.js';

const ENDPOINT = 'https://api.web3forms.com/submit';
const STORAGE_KEY = 'skynexa:pending-enquiries';
const HONEYPOT_FIELD = 'website';
const MIN_FILL_SECONDS = 3;

/** Resolve the access key from the build env or the form's own markup. */
function resolveAccessKey(form) {
  const fromEnv = import.meta.env?.VITE_WEB3FORMS_KEY;
  const fromMarkup = form?.dataset?.accessKey;
  const key = (fromEnv || fromMarkup || '').trim();
  return key && !key.startsWith('your-') ? key : null;
}

/** Read the form into a plain object keyed by field name. */
function readForm(form) {
  const data = new FormData(form);
  const payload = {};
  for (const [key, value] of data.entries()) {
    payload[key] = typeof value === 'string' ? value : String(value);
  }
  payload.consent = form.elements.consent?.checked === true;
  return payload;
}

/** Show or clear the error message attached to one field. */
function renderFieldError(form, field, message) {
  const input = form.elements[field];
  const errorNode = form.querySelector(`[data-error-for="${field}"]`);
  if (!input || !errorNode) return;

  const control = 'length' in input && !(input instanceof Element) ? input[0] : input;

  if (message) {
    control.setAttribute('aria-invalid', 'true');
    control.setAttribute('aria-describedby', `${field}-error`);
    errorNode.textContent = message;
    errorNode.hidden = false;
  } else {
    control.removeAttribute('aria-invalid');
    control.removeAttribute('aria-describedby');
    errorNode.textContent = '';
    errorNode.hidden = true;
  }
}

/** Announce form-level status into the live region. */
function setStatus(form, tone, heading, detail) {
  const region = form.querySelector('[data-form-status]');
  if (!region) return;
  region.dataset.tone = tone;
  region.hidden = false;
  region.innerHTML = '';

  const title = document.createElement('strong');
  title.textContent = heading;
  region.append(title);

  if (detail) {
    const body = document.createElement('span');
    body.textContent = detail;
    region.append(body);
  }
}

/**
 * Queue an enquiry locally so a failed send can be recovered.
 * This is a safety net, not a delivery mechanism — the UI always tells the
 * visitor to email us directly when the network send fails.
 */
function queueLocally(payload) {
  try {
    const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    existing.push({ ...payload, queuedAt: new Date().toISOString() });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(existing.slice(-20)));
    return true;
  } catch {
    // Private browsing, blocked storage, or quota — nothing more we can do.
    return false;
  }
}

/** Build a mailto: URL as the always-available escape hatch. */
function buildMailtoFallback(payload, to = 'contact@skynexa.tech') {
  const subject = `Project enquiry — ${payload.interest ?? 'General'}`;
  const body = [
    `Name: ${payload.name ?? ''}`,
    `Email: ${payload.email ?? ''}`,
    `Organisation: ${payload.company ?? '—'}`,
    `Area of interest: ${payload.interest ?? '—'}`,
    `Budget: ${payload.budget ?? '—'}`,
    `Timeline: ${payload.timeline ?? '—'}`,
    '',
    'Project brief:',
    payload.message ?? '',
  ].join('\n');
  return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/** POST the enquiry to Web3Forms. Exported so E2E tests can stub the route. */
async function sendEnquiry(payload, accessKey, { fetchImpl = fetch, signal } = {}) {
  const response = await fetchImpl(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    signal,
    body: JSON.stringify({
      access_key: accessKey,
      subject: `New project enquiry — ${payload.interest ?? 'General'}`,
      from_name: 'SkyNexa website',
      ...buildSubmission(payload),
    }),
  });

  let result;
  try {
    result = await response.json();
  } catch {
    result = {};
  }

  if (!response.ok || result.success === false) {
    throw new Error(result.message || `Submission failed (HTTP ${response.status})`);
  }
  return result;
}

/** Populate the interest <select> from the canonical catalogue. */
function populateInterests(form) {
  const select = form.elements.interest;
  if (!select || select.tagName !== 'SELECT' || select.options.length > 1) return;
  for (const interest of ENQUIRY_INTERESTS) {
    const option = document.createElement('option');
    option.value = interest;
    option.textContent = interest;
    select.append(option);
  }
}

/** Prefill the form from URL query parameters (used by service-page CTAs). */
function applyQueryPrefill(form) {
  const params = new URLSearchParams(window.location.search);
  const select = form.elements.interest;

  // `need` comes from the homepage and service-page cards and maps to an exact
  // option; `interest`/`service` are looser and matched by substring.
  // `practice` is the previous parameter name, kept so older links still work.
  const practice = params.get('need') || params.get('practice');
  const loose = params.get('interest') || params.get('service');

  if (select) {
    const mapped = practice ? PRACTICE_TO_INTEREST[practice.toLowerCase()] : null;
    if (mapped && [...select.options].some((option) => option.value === mapped)) {
      select.value = mapped;
    } else if (loose) {
      const match = [...select.options].find(
        (option) => option.value && option.value.toLowerCase().includes(loose.toLowerCase()),
      );
      if (match) select.value = match.value;
    }
  }
  const topic = params.get('topic');
  if (topic && form.elements.message && !form.elements.message.value) {
    form.elements.message.value = `Regarding ${topic}: `;
  }
}

/** Live character counter for the project brief. */
function initCounter(form) {
  const message = form.elements.message;
  const counter = form.querySelector('[data-char-count]');
  if (!message || !counter) return;

  const update = () => {
    const used = message.value.trim().length;
    counter.textContent = `${used} / ${LIMITS.message.max}`;
    counter.dataset.state =
      used > LIMITS.message.max ? 'over' : used >= LIMITS.message.min ? 'ok' : 'short';
  };
  message.addEventListener('input', update);
  update();
}

/**
 * Attach the controller to a form element.
 * @param {HTMLFormElement} form
 */
export function initEnquiryForm(form) {
  if (!form || form.dataset.enquiryReady === 'true') return;
  form.dataset.enquiryReady = 'true';

  // We do our own messaging so every error is phrased consistently and is
  // announced to assistive technology; the browser's native bubbles are not.
  form.setAttribute('novalidate', '');

  populateInterests(form);
  applyQueryPrefill(form);
  initCounter(form);

  const mountedAt = Date.now();
  const submitButton = form.querySelector('[type="submit"]');
  const submitLabel = submitButton?.textContent?.trim() ?? 'Send project brief';

  // Validate a field once it has been left, then live thereafter — validating
  // while first typing an email flags it as wrong before it can be finished.
  for (const field of ['name', 'email', 'company', 'interest', 'message']) {
    const input = form.elements[field];
    if (!input) continue;
    const validate = () => {
      const value = input.type === 'checkbox' ? input.checked : input.value;
      renderFieldError(form, field, validateField(field, value, { interests: ENQUIRY_INTERESTS }));
    };
    input.addEventListener('blur', () => {
      input.dataset.touched = 'true';
      validate();
    });
    input.addEventListener('input', () => {
      if (input.dataset.touched === 'true') validate();
    });
  }

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    const payload = readForm(form);
    const elapsedSeconds = (Date.now() - mountedAt) / 1000;
    const { valid, errors, spam, firstInvalidField } = validateEnquiry(payload, {
      interests: ENQUIRY_INTERESTS,
      honeypotField: HONEYPOT_FIELD,
      minFillSeconds: MIN_FILL_SECONDS,
      elapsedSeconds,
    });

    for (const field of ['name', 'email', 'company', 'interest', 'message', 'consent']) {
      renderFieldError(form, field, errors[field] ?? null);
      // A submit counts as touching every field, so corrections from here on
      // clear their error live. Without this, a field the user never blurred
      // keeps showing an error even after they fix it.
      const input = form.elements[field];
      if (input && 'dataset' in input) input.dataset.touched = 'true';
    }

    if (!valid) {
      if (spam && Object.keys(errors).length === 0) {
        // Silently accept-and-drop: a bot gets no signal, and on the vanishing
        // chance a human filled this in 3 seconds, they can simply resubmit.
        setStatus(
          form,
          'error',
          'Please try again.',
          'Take a moment to review the details above, then resend.',
        );
        return;
      }
      setStatus(
        form,
        'error',
        'Please check the highlighted fields.',
        'A few details need fixing before we can send this.',
      );
      const target = firstInvalidField ? form.elements[firstInvalidField] : null;
      target?.focus?.();
      return;
    }

    delete payload[HONEYPOT_FIELD];

    // Resolved at submit time, not at mount: the key can be swapped in after
    // load (by a test harness, or by markup rendered later on the page).
    const accessKey = resolveAccessKey(form);

    if (!accessKey) {
      // Honest failure beats a fake success toast.
      setStatus(
        form,
        'warning',
        'Delivery is not configured yet.',
        `This form passed validation but no submission key is set, so nothing was sent. Email ${'contact@skynexa.tech'} directly and we will pick it up.`,
      );
      const fallback = form.querySelector('[data-mailto-fallback]');
      if (fallback) {
        fallback.href = buildMailtoFallback(payload);
        fallback.hidden = false;
      }
      return;
    }

    if (submitButton) {
      submitButton.disabled = true;
      submitButton.dataset.loading = 'true';
      submitButton.textContent = 'Sending…';
    }
    setStatus(form, 'pending', 'Sending your brief…', null);

    try {
      await sendEnquiry(payload, accessKey);
      form.reset();
      initCounter(form);
      setStatus(
        form,
        'success',
        'Brief received — thank you.',
        `We have your enquiry and a senior engineer will reply to ${payload.email} within one business day.`,
      );
      form.querySelector('[data-mailto-fallback]')?.setAttribute('hidden', '');
    } catch (error) {
      const queued = queueLocally(payload);
      setStatus(
        form,
        'error',
        'We could not send that.',
        `${error.message} ${queued ? 'Your draft is saved in this browser. ' : ''}Please email contact@skynexa.tech instead — the button below opens a pre-filled message.`,
      );
      const fallback = form.querySelector('[data-mailto-fallback]');
      if (fallback) {
        fallback.href = buildMailtoFallback(payload);
        fallback.hidden = false;
      }
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
        delete submitButton.dataset.loading;
        submitButton.textContent = submitLabel;
      }
    }
  });
}

if (typeof document !== 'undefined') {
  const boot = () => {
    document.querySelectorAll('form[data-enquiry-form]').forEach(initEnquiryForm);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
}
